import { Injectable } from '@angular/core';
import {
  Amenity,
  IDtoListingBlackoutTimes,
  IDtoListingBooking,
  IDtoListingDiscount,
  IDtoListingLocation,
  IDtoSiteName,
  ILandType,
  IListing,
  IListingExtra,
  IListingWhere,
  IPolicy,
  ISite,
  ISubPhoto,
  Listing,
  Policy,
  SignalReception,
} from '@curbnturf/entities';
import * as RatingActions from '@curbnturf/rating/src/lib/+state/rating.actions';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';
import * as ListingActions from './listing.actions';
import { ListingState } from './listing.reducer';
import * as ListingSelectors from './listing.selectors';

@Injectable({ providedIn: 'root' })
export class ListingFacade {
  listings$: Observable<Listing[]>;
  entities$: Observable<{ [id: number]: Listing }>;
  listingsReadyToEnable$: Observable<Listing[]>;
  currentUserListings$: Observable<Listing[]> = of([]);
  selected$: Observable<Listing | null>;
  selectedOnce$: Observable<Listing | null>;
  error$: Observable<string | undefined>;
  loaded$: Observable<boolean>;

  constructor(private store: Store<ListingState>) {
    this.listings$ = this.store.pipe(select(ListingSelectors.getListings));
    this.entities$ = this.store.pipe(select(ListingSelectors.getListingEntities));
    this.listingsReadyToEnable$ = this.store.pipe(select(ListingSelectors.getListingsReadyToEnable));
    this.currentUserListings$ = this.store.pipe(select(ListingSelectors.getCurrentUserListings));
    this.selected$ = this.store.pipe(select(ListingSelectors.getSelectedListing));
    this.selectedOnce$ = this.selected$.pipe(
      skipWhile((listing) => !listing || !listing.id),
      take(1),
    );
    this.error$ = this.store.pipe(select(ListingSelectors.getError));
    this.loaded$ = this.store.pipe(select(ListingSelectors.getLoaded));
  }

  init() {
    this.store.dispatch(ListingActions.init());
  }

  create(listing?: IListing) {
    this.store.dispatch(ListingActions.createListing({ listing: new Listing(listing) }));
  }

  createAmenity(listing: IListing) {
    this.store.dispatch(ListingActions.createAmenity({ listing }));
  }

  createPolicy(listing: IListing) {
    this.store.dispatch(ListingActions.createPolicy({ listing }));
  }

  createSignalReception(listing: IListing) {
    this.store.dispatch(ListingActions.createSignalReception({ listing }));
  }

  remove(id: number | string) {
    this.store.dispatch(ListingActions.removeListing({ listingId: id }));
  }

  restore(id: number) {
    this.store.dispatch(ListingActions.restoreListing({ listingId: id }));
  }

  removeAmenity(listing: IListing, amenity: Amenity) {
    this.store.dispatch(ListingActions.removeAmenity({ listing, amenity }));
  }

  removePolicy(listing: IListing, policy: Policy) {
    this.store.dispatch(ListingActions.removePolicy({ listing, policy }));
  }

  removeSignalReception(listing: IListing, signalReception: SignalReception) {
    this.store.dispatch(ListingActions.removeSignalReception({ listing, signalReception }));
  }

  update(listing: IListing) {
    this.store.dispatch(ListingActions.updateListing({ listing }));
  }

  select(listingId?: number) {
    this.store.dispatch(ListingActions.selectListing({ listingId }));
  }

  selectListing(listing: IListing) {
    this.store.dispatch(ListingActions.selectListingByListing({ listing }));
  }

  loadRatings(listingId: number) {
    this.store.dispatch(RatingActions.loadListingRatings({ listingId }));
  }

  removePhoto(listingId: number, index: number): void {
    this.store.dispatch(ListingActions.removeImageFromListing({ listingId, index }));
  }

  enable(listing: IListing) {
    this.store.dispatch(ListingActions.enableListing({ listing }));
  }

  enableAndHide(listing: IListing) {
    this.store.dispatch(ListingActions.enableAndHideListing({ listing }));
  }

  disable(listing: IListing) {
    this.store.dispatch(ListingActions.disableListing({ listing }));
  }

  loaded(listing: IListing) {
    this.store.dispatch(ListingActions.listingLoaded({ listing }));
  }

  loadUserListings(userId?: number) {
    if (userId) {
      this.store.dispatch(ListingActions.loadListings({ payload: { userId } }));
    } else {
      this.store.dispatch(ListingActions.loadListings({}));
    }
  }

  loadAllListings(where?: IListingWhere) {
    this.store.dispatch(ListingActions.loadListings({ payload: where }));
  }

  load(listingId: number) {
    this.store.dispatch(ListingActions.loadListing({ listingId }));
  }

  clear() {
    this.store.dispatch(ListingActions.clearListings());
  }

  favorite(point: IListing) {
    this.store.dispatch(ListingActions.favoriteListing({ point }));
  }

  unfavorite(point: IListing) {
    this.store.dispatch(ListingActions.unfavoriteListing({ point }));
  }

  recropImage(listingToSave: IListing, photo: ISubPhoto) {
    this.store.dispatch(ListingActions.recropImage({ listingToSave, photo }));
  }

  updateSelectedListingSiteObject(site: ISite) {
    this.store.dispatch(ListingActions.updateSelectedListingSiteObject({ site }));
  }

  updateDescription(listingId: number, data: IDtoSiteName) {
    this.store.dispatch(ListingActions.updateDescription({ listingId, data }));
  }

  updatePropertyTypes(listingId: number, types: ILandType['type'][]) {
    this.store.dispatch(ListingActions.updatePropertyTypes({ listingId, types }));
  }

  updatePotentialHazards(listingId: number, potentialHazards: boolean, potentialHazardDescription: string) {
    this.store.dispatch(
      ListingActions.updatePotentialHazards({ listingId, potentialHazards, potentialHazardDescription }),
    );
  }

  updateLocation(listingId: number, data: IDtoListingLocation) {
    this.store.dispatch(ListingActions.updateLocation({ listingId, data }));
  }

  updatePolicies(listingId: number, policies: IPolicy[]) {
    this.store.dispatch(ListingActions.updatePolicies({ listingId, policies }));
  }

  updateBookingPolicy(listingId: number, data: IDtoListingBooking) {
    this.store.dispatch(ListingActions.updateBookingPolicy({ listingId, data }));
  }

  updateExtras(listingId: number, extras: IListingExtra[]) {
    this.store.dispatch(ListingActions.updateExtras({ listingId, extras }));
  }

  updateBlackoutTimes(listingId: number, blackoutTimes: IDtoListingBlackoutTimes) {
    this.store.dispatch(ListingActions.updateBlackoutTimes({ listingId, blackoutTimes }));
  }

  updatePricing(data: IDtoListingDiscount) {
    this.store.dispatch(ListingActions.updatePricing({ ...data }));
  }

  addNewSite(listing: IListing) {
    this.store.dispatch(ListingActions.addNewSite({ listing }));
  }
}
